import { useState } from "react";
import Divider from "./Divider";
import cx from "classnames";
import { motion } from "motion/react";
import { Badge } from "./Badge";
import Markdown from "react-markdown";

const PlaceholderMessage = <p className='text-center py-4 text-base-content/70'>Nothing to display yet, check back in later!</p>;

export function ExperienceAccordion({
  title,
  company,
  duration,
  logo,
  open,
  content,
  techStack,
  children,
}: {
  title: string;
  company: string;
  duration: string;
  logo?: string;
  open?: boolean;
  content?: string[] | string;
  techStack?: string[];
  children?: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(open ?? false);

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className='bg-base-200 rounded-xl shadow-lg h-full text-base-content'>
      {/* Accordion Title */}
      <div className='flex items-center p-4 lg:p-6 text-sm md:text-base lg:text-lg cursor-pointer' onClick={toggle}>
        {/* Accordion Title Content */}
        <div className='flex flex-col lg:flex-row justify-between grow lg:items-center gap-2'>
          <div className='flex items-center gap-4'>
            {logo && (
              <div className='w-24 h-12 flex items-center'>
                <img src={logo} alt={`${company} logo`} className='object-contain size-full' />
              </div>
            )}
            <div>
              <h4 className='text-lg md:text-xl lg:text-2xl font-semibold'>{title}</h4>
              <p className='text-base-content/85 mt-1'>{company}</p>
            </div>
          </div>

          <div className='flex lg:flex-col items-center lg:items-end gap-1 self-start lg:self-auto'>
            <p className='text-base-content/70'>{duration}</p>
            <p className='text-base-content/70 text-sm text-right'>Singapore</p>
          </div>
        </div>
        {/* Accordion Caret */}
        <svg
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
          strokeWidth={1.5}
          stroke='currentColor'
          className={cx("size-6 ml-6 transition-transform duration-250 ease-in-out", { "rotate-180": isOpen })}
        >
          <path strokeLinecap='round' strokeLinejoin='round' d='m19.5 8.25-7.5 7.5-7.5-7.5' />
        </svg>
      </div>
      {/* Accordion Content */}
      <motion.div
        initial={false}
        animate={isOpen ? "open" : "collapsed"}
        variants={{
          open: { height: "auto", opacity: 1 },
          collapsed: { height: 0, opacity: 0 },
        }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className='overflow-hidden px-6'
      >
        <Divider className='border!' />
        <div className='py-6 prose prose-h4:leading-relaxed max-w-none leading-snug'>
          {Array.isArray(content) ? (
            <>
              {/* {content.map((item, index) => (
                  <li key={index} className=''>
                    <span className='px-2'>• </span>
                    {item}
                  </li>
                ))} */}
              <Markdown>{content.join("\n")}</Markdown>
              {techStack && (
                <div className='flex flex-wrap gap-2 mt-6'>
                  {techStack.map((tech, index) => (
                    <Badge key={index} text={tech} className='text-base-content/70' />
                  ))}
                </div>
              )}
            </>
          ) : typeof content === "string" ? (
            <Markdown>{content}</Markdown>
          ) : (
            (children ?? PlaceholderMessage)
          )}
        </div>
      </motion.div>
    </div>
  );
}
